import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, FormControl, IconButton, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { CheckCircleOutline } from '@mui/icons-material'
import { getFileSizeFromBytes, Icons, secondsToHMS } from '../../../utils/utilities'
import VideoPlayer from '../../common/video-player/VideoPlayer'
import VisuallyHiddenInput from '../../common/VisuallyHiddenInput'
import Progress from '../../common/Progress'
import CustomChip from '../../common/CustomChip'
import { useSettingsStore } from '../../../store/settings.store'
import api from '../../../service/api'

const TUTORIAL_ROLES = [
    {
        value: 'STUDENT',
        label: 'Student',
    },
    {
        value: 'FACULTY',
        label: 'Faculty',
    },
    {
        value: 'HOD',
        label: 'HOD',
    },
    {
        value: 'PRINCIPAL',
        label: 'Principal',
    },
]

const EMPTY_FORM = {
    audiences: ['FACULTY'],
    module: '',
    title: '',
    description: '',
    display_order: 1,
}

const DEFAULT_UPLOAD_STATUS = {
    progress: 0,
    status: 'Queued',
    statusId: 1,
}

const getVideoMetadata = ( url ) =>
    new Promise( ( resolve, reject ) => {
        const video = document.createElement( 'video' )
        video.src = url
        video.preload = 'metadata'
        video.onloadedmetadata = () => {
            let videoOrientation = 'square'
            if ( video.videoWidth > video.videoHeight ) {
                videoOrientation = 'landscape'
            } else if ( video.videoHeight > video.videoWidth ) {
                videoOrientation = 'portrait'
            }
            resolve( {
                videoHeight: video.videoHeight,
                videoWidth: video.videoWidth,
                duration: video.duration,
                durationMs: video.duration * 1000,
                videoOrientation,
            } )
        }
        video.onerror = () => {
            reject( new Error( 'Failed to read video metadata.' ) )
        }
    } )


const AddTutorialDialog = ( { open, setOpen, tutorial = null, refresh } ) => {

    const [form, setForm] = useState( EMPTY_FORM )
    const [selectedFile, setSelectedFile] = useState( null )
    const [previewUrl, setPreviewUrl] = useState( null )
    const [uploading, setUploading] = useState( false )
    const [uploadStatus, setUploadStatus] = useState( DEFAULT_UPLOAD_STATUS )

    const { palette, border, breakpoints } = useTheme()
    const xs = useMediaQuery( breakpoints.down( 'sm' ) )

    const SettingsStore = useSettingsStore()
    const isEditMode = Boolean( tutorial )

    useEffect( () => {
        if ( !open ) {
            return
        }
        setForm(
            tutorial
                ? {
                    audiences: Array.isArray( tutorial.audiences ) && tutorial.audiences.length > 0 ? tutorial.audiences : ['FACULTY'],
                    module: tutorial.module || '',
                    title: tutorial.title || '',
                    description: tutorial.description || '',
                    display_order: tutorial.display_order ?? 1,
                }
                : {
                    ...EMPTY_FORM,
                }
        )
        setSelectedFile( null )
        setPreviewUrl( null )
        setUploadStatus( DEFAULT_UPLOAD_STATUS )
    }, [open, tutorial] )


    useEffect( () => {
        return () => {
            if ( previewUrl ) {
                URL.revokeObjectURL( previewUrl )
            }
        }
    }, [previewUrl] )


    const updateStatus = ( progress, status, statusId ) => {
        setUploadStatus( {
            progress,
            status,
            statusId,
        } )
    }

    const handleChange = ( event ) => {
        const { name, value } = event.target
        setForm( ( prev ) => ( {
            ...prev,
            [name]: value,
        } ) )
    }

    // TOGGLE ROLE
    const toggleRole = ( role ) => {
        if ( uploading ) {
            return
        }
        setForm( ( prev ) => {
            const selected = prev.audiences.includes( role )
            return {
                ...prev,
                audiences: selected ? prev.audiences.filter( ( item ) => item !== role ) : [...prev.audiences, role],
            }
        } )
    }

    const resetDialog = () => {
        if ( previewUrl ) {
            URL.revokeObjectURL( previewUrl )
        }
        setSelectedFile( null )
        setPreviewUrl( null )
        setUploadStatus( DEFAULT_UPLOAD_STATUS )
    }

    const closeDialog = () => {
        if ( uploading ) {
            return
        }
        resetDialog()
        setOpen( false )
    }

    const handleNewVideoFileSelection = async ( event ) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if ( !file ) {
            return
        }
        try {
            const parts = file.name.split( '.' )
            const ext = parts.pop()?.toLowerCase()
            if ( ext !== 'mp4' ) {
                return toast( `Invalid file format: ${file.name}. Only .mp4 video file is supported.` )
            }
            const maxFileSize = SettingsStore?.getUseSettings?.['max_tutorial_video_size'] || SettingsStore?.getUseSettings?.['max_hybrid_class_video_size'] || 300
            if ( file.size > maxFileSize * 1024 * 1024 ) {
                return toast( `File too large: ${file.name}. Maximum size supported ${maxFileSize}MB.` )
            }
            const fileName = parts.join( '.' )
            const blobUrl = URL.createObjectURL( file )
            const metadata = await getVideoMetadata( blobUrl )
            file.fileName = fileName
            file.ext = ext
            file.metadata = metadata
            if ( previewUrl ) {
                URL.revokeObjectURL( previewUrl )
            }
            setSelectedFile( file )
            setPreviewUrl( blobUrl )
            if ( !form.title?.trim() ) {
                setForm( ( prev ) => ( {
                    ...prev,
                    title: fileName,
                } ) )
            }
        } catch ( err ) {
            console.log( 'Tutorial video verification error:', err )
            toast( 'Failed to verify the selected video file! Please try again.' )
        }
    }

    const validateForm = () => {
        if ( !Array.isArray( form.audiences ) || form.audiences.length === 0 ) {
            toast( 'Please select at least one role.' )
            return false
        }
        const invalidAudience = form.audiences.find( ( role ) => !TUTORIAL_ROLES.some( ( item ) => item.value === role ) )
        if ( invalidAudience ) {
            toast( 'Please select valid tutorial roles.' )
            return false
        }
        if ( !form.module?.trim() ) {
            toast( 'Please enter a module name.' )
            return false
        }
        if ( !form.title?.trim() ) {
            toast( 'Please enter a tutorial title.' )
            return false
        }
        const displayOrder = Number( form.display_order )
        if ( Number.isNaN( displayOrder ) || displayOrder < 0 ) {
            toast( 'Display order cannot be negative.' )
            return false
        }
        if ( !isEditMode && !selectedFile ) {
            toast( 'Please select a tutorial video.' )
            return false
        }
        return true
    }

    const buildFormData = () => {
        const formData = new FormData()
        if ( selectedFile ) {
            formData.append( 'video', selectedFile, selectedFile.name )
            formData.append( 'file_metadata', JSON.stringify( selectedFile.metadata || {} ) )
        }
        formData.append( 'audiences', JSON.stringify( form.audiences ) )
        formData.append( 'module', form.module.trim() )
        formData.append( 'title', form.title.trim() )
        formData.append( 'description', form.description?.trim() || '' )
        formData.append( 'display_order', String( Number( form.display_order ) || 0 ) )
        return formData
    }

    const uploadTutorial = async () => {
        if ( !validateForm() ) {
            return
        }
        try {
            setUploading( true )
            updateStatus( 0, isEditMode ? 'Updating tutorial...' : 'Uploading tutorial...', 3 )
            const formData = buildFormData()
            const progressHandler = {
                onUploadProgress: ( event ) => {
                    if ( !event.total ) {
                        return
                    }
                    const progress = Math.round( ( event.loaded * 100 ) / event.total )
                    updateStatus( progress, 'Uploading...', 3 )
                },
            }
            if ( isEditMode ) {
                await api.tutorialsAndManuals.updateTutorial( tutorial.id, formData, progressHandler )
            } else {
                await api.tutorialsAndManuals.addTutorial( formData, progressHandler )
            }
            updateStatus( 100, 'Completed', 5 )
            toast.success( isEditMode ? 'Tutorial updated successfully.' : 'Tutorial uploaded successfully.' )
            if ( typeof refresh === 'function' ) {
                await refresh()
            }
            resetDialog()
            setOpen( false )
        } catch ( err ) {
            console.log( 'Tutorial upload error:', err )
            console.log( 'Tutorial upload response:', err?.response )
            updateStatus( 0, 'Failed', -1 )
            if ( err?.response?.status === 413 ) {
                toast( 'Tutorial video is too large.' )
                return
            }
            toast( err?.response?.data?.message || err?.message || 'Failed to upload the tutorial video! Please try again.' )
        } finally {
            setUploading( false )
        }
    }

    const getVideoHeight = () => {
        const metadata = selectedFile?.metadata || tutorial?.file_details
        if ( metadata?.videoOrientation === 'portrait' ) {
            return '350px'
        }
        if ( metadata?.videoOrientation === 'square' ) {
            return '400px'
        }
        return 'auto'
    }


    return (
        <Dialog
            disableRestoreFocus
            fullScreen={xs}
            open={open}
            onClose={closeDialog}
            slotProps={{
                paper: {
                    sx: {
                        width: {
                            xs: '100%',
                            sm: '90%',
                        },
                        maxWidth: '700px',
                        maxHeight: '90vh',
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    borderBottom: border[1],
                    zIndex: 5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '20px',
                    alignItems: 'center',
                    padding: '10px 20px',
                }}
            >
                <Typography variant='h1' fontWeight='600' component='span' fontSize='16px'>
                    {isEditMode ? `Edit tutorial: ${tutorial?.title || ''}` : 'Upload new tutorial'}
                </Typography>

                <IconButton onClick={closeDialog} disabled={uploading}>
                    {Icons.default.CloseIcon}
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    padding: 0,
                }}
            >
                <Box padding='20px'>
                    {/* TITLE */}

                    <FormControl margin='normal' fullWidth>
                        <Typography gutterBottom variant='subtitle2' color={palette.form.labelColor}>
                            What is this tutorial about? *
                        </Typography>

                        <TextField size='small' name='title' value={form.title} disabled={uploading} onChange={handleChange} placeholder='Eg: How to create an assignment' />
                    </FormControl>

                    {/* DESCRIPTION */}

                    <FormControl margin='normal' fullWidth>
                        <Typography gutterBottom variant='subtitle2' color={palette.form.labelColor}>
                            Explain in details here...
                        </Typography>

                        <TextField size='small' multiline minRows={4} name='description' value={form.description} disabled={uploading} onChange={handleChange} placeholder='Explain what the user will learn from this tutorial...' />
                    </FormControl>

                    {/* MODULE */}

                    <FormControl margin='normal' fullWidth>
                        <Typography gutterBottom variant='subtitle2' color={palette.form.labelColor}>
                            Select the module for this tutorial... *
                        </Typography>

                        <TextField size='small' name='module' value={form.module} disabled={uploading} onChange={handleChange} placeholder='Eg: Assignments' />
                    </FormControl>

                    {/* ROLES */}

                    <FormControl margin='normal' fullWidth>
                        <Typography gutterBottom variant='subtitle2' color={palette.form.labelColor}>
                            Select the user group that can view this tutorial... *
                        </Typography>

                        <Box display='flex' flexWrap='wrap' gap='10px'>
                            {TUTORIAL_ROLES.map( ( role ) => {
                                const selected = form.audiences.includes( role.value )
                                return (
                                    <Box
                                        key={role.value}
                                        borderRadius='30px'
                                        border={`2px solid ${palette.borderColor}`}
                                        display='flex'
                                        gap='10px'
                                        padding='5px 15px'
                                        onClick={() => toggleRole( role.value )}
                                        bgcolor={selected ? palette.success.main : palette.greyedOut}
                                        sx={{
                                            transition: '300ms',
                                            cursor: uploading ? 'not-allowed' : 'pointer',
                                            opacity: uploading ? 0.6 : 1,
                                            borderColor: selected ? palette.success.main : palette.borderColor,
                                            '&:hover': {
                                                borderColor: selected ? palette.success.main : palette.common.font,
                                            },
                                        }}
                                        color={selected ? palette.common.white : palette.common.font}
                                    >
                                        {selected && (
                                            <CheckCircleOutline
                                                sx={{
                                                    color: 'inherit',
                                                }}
                                                fontSize='small'
                                            />
                                        )}

                                        <Typography color={selected ? palette.common.white : palette.common.font} fontWeight='500' fontSize='14px'>
                                            {role.label}
                                        </Typography>
                                    </Box>
                                )
                            } )}
                        </Box>
                    </FormControl>

                    {/* VIDEO */}

                    <FormControl margin='normal' fullWidth>
                        <Typography gutterBottom variant='subtitle2' color={palette.form.labelColor}>
                            Tutorial video *
                        </Typography>

                        <Box borderRadius='5px' border={border[1]} bgcolor={palette.form.inputBg} padding='12px'>
                            <Box display='flex' alignItems='center' gap='10px' flexWrap='wrap'>
                                <Button
                                    tabIndex={-1}
                                    component='label'
                                    size='small'
                                    color='greyed'
                                    sx={{
                                        textTransform: 'capitalize',
                                    }}
                                    startIcon={Icons.default.CloudUpload}
                                    disabled={uploading}
                                >
                                    {selectedFile ? 'Change video' : isEditMode ? 'Replace video' : 'Select video'}

                                    <VisuallyHiddenInput accept='video/mp4' type='file' onChange={handleNewVideoFileSelection} />
                                </Button>

                                {selectedFile && (
                                    <Typography fontSize='12px' color='textSecondary'>
                                        {selectedFile.name}
                                    </Typography>
                                )}
                            </Box>

                            {selectedFile && (
                                <Box display='flex' gap='10px' alignItems='center' flexWrap='wrap' marginTop='12px'>
                                    <CustomChip startIcon={Icons.default.Storage} value={`Size: ${getFileSizeFromBytes( selectedFile.size )}`} />

                                    {selectedFile?.metadata?.duration && <CustomChip startIcon={Icons.default.Clock} value={`Duration: ${secondsToHMS( selectedFile.metadata.duration, false )}`} />}

                                    {selectedFile?.metadata?.videoOrientation && <CustomChip startIcon={selectedFile.metadata.videoOrientation === 'portrait' ? Icons.default.CropPortrait : selectedFile.metadata.videoOrientation === 'landscape' ? Icons.default.CropLandscape : Icons.default.CropSquare} value={`Orientation: ${selectedFile.metadata.videoOrientation}`} />}
                                </Box>
                            )}
                        </Box>
                    </FormControl>

                    {/* PREVIEW */}

                    {( previewUrl || ( isEditMode && tutorial?.video_url ) ) && (
                        <FormControl margin='normal' fullWidth>
                            <Typography gutterBottom variant='subtitle2' color={palette.form.labelColor}>
                                Video preview
                            </Typography>

                            <Box borderRadius='5px' overflow='hidden' border={border[1]}>
                                <VideoPlayer title={previewUrl ? form.title || selectedFile?.fileName || 'Tutorial Preview' : tutorial.title} src={previewUrl || tutorial.video_url} containerHeight={getVideoHeight()} controls={false} />
                            </Box>
                        </FormControl>
                    )}

                    {/* DISPLAY ORDER */}

                    <FormControl margin='normal' sx={{ maxWidth: '250px', }} >
                        <Typography gutterBottom variant='subtitle2' color={palette.form.labelColor}>
                            Display Order
                        </Typography>

                        <TextField
                            size='small'
                            name='display_order'
                            type='number'
                            value={form.display_order}
                            disabled={uploading}
                            onChange={handleChange}
                            inputProps={{
                                min: 0,
                            }}
                        />
                    </FormControl>

                    {/* PROGRESS */}

                    {[2, 3, 4, 5].includes( uploadStatus.statusId ) && (
                        <Box display='flex' alignItems='center' marginTop='15px' gap='10px'>
                            <Progress size={20} trackWidth={4} indicatorWidth={4} showProgress={false} progress={uploadStatus.progress} />

                            <Typography fontSize='12px' component='em' color='textSecondary'>
                                {uploadStatus.status} {uploadStatus.progress}%
                            </Typography>
                        </Box>
                    )}

                    {/* ACTIONS */}

                    <Box display='flex' gap='20px' marginTop='40px' flexWrap='wrap'>
                        <Button
                            disableElevation
                            disabled={uploading}
                            onClick={uploadTutorial}
                            startIcon={uploading ? <CircularProgress size={14} /> : Icons.default.CloudUpload}
                            variant='contained'
                            sx={{
                                textTransform: 'capitalize',
                            }}
                        >
                            {uploading ? ( isEditMode ? 'Saving changes...' : 'Uploading tutorial...' ) : isEditMode ? 'Save changes' : 'Upload Tutorial'}
                        </Button>

                        <Button
                            disableElevation
                            color='error'
                            onClick={closeDialog}
                            disabled={uploading}
                            variant='contained'
                            sx={{
                                textTransform: 'capitalize',
                            }}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    )
}
export default AddTutorialDialog
